/**
 * ZAXIA Mock Hotel Lab Data
 * Static mock data for frontend development
 * Mirrors backend Hotel Lab seed data
 */

import type {
  Hotel,
  Room,
  Guest,
  Stay,
  ServiceCatalogItem,
  CalendarEvent,
  TVChannel,
  ServiceCardData,
  WeatherData,
  Message,
  Booking,
  BillCharge,
  HotelBill,
  ServiceDetail,
} from '@/types';
import {
  ServiceCategory,
  RoomStatus,
  RoomType,
  StayStatus,
  CalendarEventStatus,
  BookingStatus,
  ChargeCategory,
} from '@/types';

// =============================================================================
// HOTEL
// =============================================================================

export const mockHotel: Hotel = {
  hotelId: 'hotel_lab_001',
  name: 'Hotel Lab Demo',
  timezone: 'Europe/Lisbon',
  currency: 'EUR',
  defaultLanguage: 'en',
  languages: ['en', 'pt', 'es', 'fr', 'de'],
  operatingHours: {
    spa: { open: '09:00', close: '21:00' },
    restaurant: {
      breakfast: { open: '07:00', close: '10:30' },
      lunch: { open: '12:00', close: '15:00' },
      dinner: { open: '19:00', close: '22:30' },
    },
  },
  policies: {
    lateCheckoutFee: 50,
    lateCheckoutMaxHour: '14:00',
    minibarPosting: false,
  },
};

// =============================================================================
// CURRENT GUEST SESSION (Room 1501 - João & Maria)
// =============================================================================

export const currentGuest: Guest = {
  guestId: 'guest_001',
  firstName: 'João',
  lastName: 'Silva',
  email: 'joao.silva@email.com',
  phone: '+351912345678',
  language: 'pt',
  preferences: {
    spaInterest: true,
    diningInterest: true,
    sustainabilityOptIn: true,
  },
};

export const currentRoom: Room = {
  roomId: 'room_1501',
  hotelId: mockHotel.hotelId,
  roomNumber: '1501',
  roomType: RoomType.SUITE,
  floor: 15,
  status: RoomStatus.OCCUPIED,
  tvId: 'TV-1501',
  currentStayId: 'stay_001',
};

export const currentStay: Stay = {
  stayId: 'stay_001',
  hotelId: mockHotel.hotelId,
  roomId: currentRoom.roomId,
  guestId: currentGuest.guestId,
  checkInDate: '2026-02-20',
  checkOutDate: '2026-02-27',
  actualCheckIn: '2026-02-20T14:30:00',
  actualCheckOut: null,
  status: StayStatus.CHECKED_IN,
  greenPreferences: {
    greenStayOptIn: true,
    skipCleaningDates: [],
    towelsOnlyDates: [],
  },
};

// =============================================================================
// ALL ROOMS
// =============================================================================

export const mockRooms: Room[] = [
  currentRoom,
  { roomId: 'room_101', hotelId: mockHotel.hotelId, roomNumber: '101', roomType: RoomType.STANDARD, floor: 1, status: RoomStatus.VACANT, tvId: 'TV-101', currentStayId: null },
  { roomId: 'room_102', hotelId: mockHotel.hotelId, roomNumber: '102', roomType: RoomType.STANDARD, floor: 1, status: RoomStatus.OCCUPIED, tvId: 'TV-102', currentStayId: 'stay_102' },
  { roomId: 'room_201', hotelId: mockHotel.hotelId, roomNumber: '201', roomType: RoomType.DELUXE, floor: 2, status: RoomStatus.CLEANING, tvId: 'TV-201', currentStayId: null },
  { roomId: 'room_305', hotelId: mockHotel.hotelId, roomNumber: '305', roomType: RoomType.DELUXE, floor: 3, status: RoomStatus.OCCUPIED, tvId: 'TV-305', currentStayId: 'stay_305' },
];

// =============================================================================
// SERVICES
// =============================================================================

export const mockServices: ServiceCatalogItem[] = [
  // Housekeeping
  { serviceId: 'svc_001', hotelId: mockHotel.hotelId, category: ServiceCategory.HOUSEKEEPING, name: 'Extra Towels', description: 'Fresh towels delivered to your room', durationMinutes: 15, price: null, capacity: 10, available: true },
  { serviceId: 'svc_002', hotelId: mockHotel.hotelId, category: ServiceCategory.HOUSEKEEPING, name: 'Room Cleaning', description: 'Full room cleaning service', durationMinutes: 45, price: null, capacity: 5, available: true },
  { serviceId: 'svc_003', hotelId: mockHotel.hotelId, category: ServiceCategory.HOUSEKEEPING, name: 'Pillow Request', description: 'Additional pillows for your comfort', durationMinutes: 10, price: null, capacity: 10, available: true },
  // Spa
  { serviceId: 'svc_004', hotelId: mockHotel.hotelId, category: ServiceCategory.SPA, name: 'Swedish Massage', description: 'Relaxing full-body massage', durationMinutes: 60, price: 90, capacity: 2, available: true },
  { serviceId: 'svc_005', hotelId: mockHotel.hotelId, category: ServiceCategory.SPA, name: 'Facial Treatment', description: 'Rejuvenating facial care', durationMinutes: 45, price: 75, capacity: 2, available: true },
  { serviceId: 'svc_006', hotelId: mockHotel.hotelId, category: ServiceCategory.SPA, name: 'Couples Massage', description: 'Side-by-side massage for two', durationMinutes: 90, price: 180, capacity: 1, available: true },
  // Restaurant
  { serviceId: 'svc_007', hotelId: mockHotel.hotelId, category: ServiceCategory.RESTAURANT, name: 'Breakfast Buffet', description: 'Full breakfast buffet', durationMinutes: 60, price: 25, capacity: 50, available: true },
  { serviceId: 'svc_008', hotelId: mockHotel.hotelId, category: ServiceCategory.RESTAURANT, name: 'Dinner Reservation', description: 'Table reservation at restaurant', durationMinutes: 90, price: null, capacity: 20, available: true },
  // Room Service
  { serviceId: 'svc_009', hotelId: mockHotel.hotelId, category: ServiceCategory.ROOM_SERVICE, name: 'Room Service', description: 'Food and drinks delivered', durationMinutes: 30, price: null, capacity: 10, available: true },
];

// =============================================================================
// CALENDAR EVENTS
// =============================================================================

export const mockCalendarEvents: CalendarEvent[] = [
  {
    eventId: 'evt_001',
    stayId: currentStay.stayId,
    serviceType: ServiceCategory.SPA,
    title: 'Spa: Swedish Massage',
    datetime: '2026-02-23T16:00:00',
    durationMinutes: 60,
    location: 'Wellness Center, Level 2',
    sourceBookingId: 'bkg_001',
    sourceTaskId: null,
    status: CalendarEventStatus.CONFIRMED,
  },
  {
    eventId: 'evt_002',
    stayId: currentStay.stayId,
    serviceType: ServiceCategory.RESTAURANT,
    title: 'Restaurant: Dinner Reservation',
    datetime: '2026-02-23T20:00:00',
    durationMinutes: 90,
    location: 'Main Restaurant',
    sourceBookingId: 'bkg_002',
    sourceTaskId: null,
    status: CalendarEventStatus.CONFIRMED,
  },
  {
    eventId: 'evt_003',
    stayId: currentStay.stayId,
    serviceType: ServiceCategory.SPA,
    title: 'Spa: Couples Massage',
    datetime: '2026-02-24T14:00:00',
    durationMinutes: 90,
    location: 'Wellness Center, Level 2',
    sourceBookingId: 'bkg_003',
    sourceTaskId: null,
    status: CalendarEventStatus.CONFIRMED,
  },
];

// =============================================================================
// TV CHANNELS
// =============================================================================

export const mockTVChannels: TVChannel[] = [
  { id: 'ch_hbo', name: 'HBO', number: 1, currentProgram: 'The Last of Us', logo: '🎬' },
  { id: 'ch_cnn', name: 'CNN', number: 2, currentProgram: 'World News Tonight', logo: '📰' },
  { id: 'ch_bbc', name: 'BBC', number: 3, currentProgram: 'Planet Earth III', logo: '🌍' },
  { id: 'ch_espn', name: 'ESPN', number: 4, currentProgram: 'NBA Tonight', logo: '🏀' },
  { id: 'ch_discovery', name: 'Discovery', number: 5, currentProgram: 'MythBusters', logo: '🔬' },
  { id: 'ch_streaming', name: 'Streaming', number: 6, currentProgram: 'Access streaming services', logo: '📺' },
];

// =============================================================================
// SERVICE CARDS (8 total for main grid)
// =============================================================================

export const mockServiceCards: ServiceCardData[] = [
  { id: 'card_room_service', title: 'Room Service', category: ServiceCategory.ROOM_SERVICE, icon: '🍽️', image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&h=300&fit=crop', description: 'Order meals & drinks' },
  { id: 'card_restaurant', title: 'Restaurant', category: ServiceCategory.RESTAURANT, icon: '🍷', image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400&h=300&fit=crop', description: 'Reserve tables' },
  { id: 'card_bar', title: 'Bar', category: ServiceCategory.BAR, icon: '🍸', image: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=400&h=300&fit=crop', description: 'Rooftop cocktails' },
  { id: 'card_pool', title: 'Pool', category: ServiceCategory.ACTIVITY, icon: '🏊', image: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=400&h=300&fit=crop', description: 'Infinity pool access' },
  { id: 'card_spa', title: 'SPA', category: ServiceCategory.SPA, icon: '💆', image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&h=300&fit=crop', description: 'Luxury treatments' },
  { id: 'card_gym', title: 'Gym', category: ServiceCategory.ACTIVITY, icon: '🏋️', image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&h=300&fit=crop', description: 'Fitness center' },
  { id: 'card_explore', title: 'Explore', category: ServiceCategory.HOTEL_INFO, icon: '🗺️', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop', description: 'Local attractions' },
  { id: 'card_transport', title: 'Transport', category: ServiceCategory.TRANSPORT, icon: '🚗', image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=400&h=300&fit=crop', description: 'Car service' },
];

// =============================================================================
// WEATHER
// =============================================================================

export const mockWeather: WeatherData = {
  temperature: 18,
  condition: 'partly-cloudy',
  humidity: 65,
  location: 'Lisbon',
};

// =============================================================================
// MESSAGES
// =============================================================================

export const mockMessages: Message[] = [
  {
    id: 'msg_001',
    from: 'Front Desk',
    title: 'Welcome to Hotel & Lab',
    content: 'Dear Guest, welcome to your stay at Hotel & Lab. We hope you enjoy all our amenities. The spa is open daily until 9 PM, and dinner reservations can be made at any time through this concierge. Do not hesitate to reach out if there is anything we can do to make your stay more comfortable.',
    timestamp: '2026-02-22T15:00:00',
    read: true,
    type: 'welcome',
  },
  {
    id: 'msg_002',
    from: 'Spa & Wellness',
    title: 'Spa Booking Confirmed',
    content: 'Your Swedish Massage has been confirmed for February 24th at 4:00 PM at the Wellness Center, Level 2. Please arrive 10 minutes early to complete a short wellness questionnaire. Robes and slippers will be provided.',
    timestamp: '2026-02-22T17:30:00',
    read: true,
    type: 'info',
  },
  {
    id: 'msg_003',
    from: 'Hotel Concierge',
    title: 'Special Offer: Wine Tasting Tonight',
    content: 'Join us this evening for a complimentary wine tasting at the Lobby Bar from 6–8 PM. We will be featuring a curated selection of local Portuguese wines paired with artisan cheeses. Attendance is limited — reply to this message to reserve your spot.',
    timestamp: '2026-02-23T09:00:00',
    read: false,
    type: 'alert',
  },
  {
    id: 'msg_004',
    from: 'Restaurant',
    title: 'Dinner Reservation — Tonight at 8 PM',
    content: 'This is a friendly reminder that your table for 2 at the Rooftop Restaurant is confirmed for tonight at 8:00 PM. Our chef has prepared a special tasting menu for this evening. Should you need to change the time or party size, please let us know before 6 PM.',
    timestamp: '2026-02-23T11:15:00',
    read: false,
    type: 'info',
  },
  {
    id: 'msg_005',
    from: 'Housekeeping',
    title: 'Green Stay — Thank You',
    content: 'Thank you for participating in our Green Stay programme. By skipping daily cleaning, you are helping us save approximately 40 litres of water and reduce chemical use. Your room will still receive a full refresh every 3 days. Fresh towels are available on request at any time.',
    timestamp: '2026-02-23T08:00:00',
    read: false,
    type: 'info',
  },
];

// =============================================================================
// SPECIAL OFFERS (for TV section carousel)
// =============================================================================

export const mockSpecialOffers = [
  { id: 'offer_001', title: 'Wine Tasting', description: 'Tonight at Lobby Bar', discount: 'FREE' },
  { id: 'offer_002', title: 'Spa Package', description: '20% off couples massage', discount: '-20%' },
  { id: 'offer_003', title: 'Late Checkout', description: 'Extend until 2 PM', discount: '€50' },
];

// =============================================================================
// SERVICE DETAILS (Phase 2B)
// =============================================================================

export const mockServiceDetails: ServiceDetail[] = [
  {
    id: 'card_room_service',
    title: 'Room Service',
    category: ServiceCategory.ROOM_SERVICE,
    icon: '🍽️',
    images: [
      'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=800&h=600&fit=crop',
    ],
    description: 'Order meals & drinks',
    longDescription: 'Enjoy delicious meals and beverages delivered directly to your room. Our kitchen is open 24/7 with a curated selection of local and international cuisine.',
    operatingHours: '24 hours',
    options: [
      { optionId: 'rs_breakfast', name: 'Breakfast Menu', description: 'Continental & full English breakfast', price: null, available: true },
      { optionId: 'rs_lunch', name: 'Lunch Menu', description: 'Light bites and main courses', price: null, available: true },
      { optionId: 'rs_dinner', name: 'Dinner Menu', description: 'Fine dining experience in room', price: null, available: true },
      { optionId: 'rs_drinks', name: 'Beverages', description: 'Wines, cocktails, soft drinks', price: null, available: true },
    ],
  },
  {
    id: 'card_restaurant',
    title: 'Restaurant',
    category: ServiceCategory.RESTAURANT,
    icon: '🍷',
    images: [
      'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&h=600&fit=crop',
    ],
    description: 'Reserve tables',
    longDescription: 'Our award-winning restaurant offers Mediterranean cuisine with stunning views. Chef Miguel crafts seasonal menus using locally sourced ingredients.',
    location: 'Ground Floor, Main Building',
    operatingHours: 'Breakfast 7:00-10:30, Lunch 12:00-15:00, Dinner 19:00-22:30',
    options: [
      { optionId: 'rest_breakfast', name: 'Breakfast Buffet', description: 'Full buffet with live cooking stations', price: 25, durationMinutes: 60, available: true },
      { optionId: 'rest_lunch', name: 'Lunch Reservation', description: 'Table for 2-4 guests', price: null, durationMinutes: 90, available: true },
      { optionId: 'rest_dinner', name: 'Dinner Reservation', description: 'Fine dining experience', price: null, durationMinutes: 120, available: true },
    ],
  },
  {
    id: 'card_bar',
    title: 'Bar',
    category: ServiceCategory.BAR,
    icon: '🍸',
    images: [
      'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1536935338788-846bb9981813?w=800&h=600&fit=crop',
    ],
    description: 'Rooftop cocktails',
    longDescription: 'Experience our rooftop bar with panoramic city views. Expert mixologists craft signature cocktails alongside an extensive wine and spirits menu.',
    location: 'Rooftop, Level 8',
    operatingHours: '17:00-01:00',
    options: [
      { optionId: 'bar_table', name: 'Table Reservation', description: 'Reserve a table with city view', price: null, durationMinutes: 120, available: true },
      { optionId: 'bar_vip', name: 'VIP Section', description: 'Exclusive corner with bottle service', price: 200, durationMinutes: 180, available: true },
    ],
  },
  {
    id: 'card_pool',
    title: 'Pool',
    category: ServiceCategory.ACTIVITY,
    icon: '🏊',
    images: [
      'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&h=600&fit=crop',
    ],
    description: 'Infinity pool access',
    longDescription: 'Our stunning infinity pool offers breathtaking views over the city. Heated year-round with dedicated sunbeds and poolside service.',
    location: 'Level 3, Outdoor Terrace',
    operatingHours: '07:00-21:00',
    options: [
      { optionId: 'pool_sunbed', name: 'Reserve Sunbed', description: 'Premium sunbed with umbrella', price: 25, durationMinutes: 480, available: true,
        sessionSlots: [
          { id: 'full_day',   label: 'Full Day',  icon: '☀️', time: '07:00', durationLabel: '07:00 – 21:00', durationMinutes: 840, price: 50  },
          { id: 'morning',   label: 'Morning',   icon: '🌅', time: '07:00', durationLabel: '07:00 – 13:00', durationMinutes: 360, price: 25  },
          { id: 'afternoon', label: 'Afternoon', icon: '🌤️', time: '13:00', durationLabel: '13:00 – 21:00', durationMinutes: 480, price: 30  },
        ],
      },
      { optionId: 'pool_cabana', name: 'Private Cabana', description: 'Exclusive poolside cabana', price: 75, durationMinutes: 480, available: true,
        sessionSlots: [
          { id: 'full_day',   label: 'Full Day',  icon: '☀️', time: '07:00', durationLabel: '07:00 – 21:00', durationMinutes: 840, price: 150 },
          { id: 'morning',   label: 'Morning',   icon: '🌅', time: '07:00', durationLabel: '07:00 – 13:00', durationMinutes: 360, price: 75  },
          { id: 'afternoon', label: 'Afternoon', icon: '🌤️', time: '13:00', durationLabel: '13:00 – 21:00', durationMinutes: 480, price: 75  },
        ],
      },
    ],
    sessionSlots: undefined,
  },
  {
    id: 'card_spa',
    title: 'SPA',
    category: ServiceCategory.SPA,
    icon: '💆',
    images: [
      'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&h=600&fit=crop',
    ],
    description: 'Luxury treatments',
    longDescription: 'Indulge in our full-service spa featuring traditional and modern treatments. Our expert therapists use premium organic products.',
    location: 'Wellness Center, Level 2',
    operatingHours: '09:00-21:00',
    options: [
      { optionId: 'spa_swedish', name: 'Swedish Massage', description: 'Relaxing full-body massage', price: 90, durationMinutes: 60, available: true },
      { optionId: 'spa_facial', name: 'Facial Treatment', description: 'Rejuvenating facial care', price: 75, durationMinutes: 45, available: true },
      { optionId: 'spa_couples', name: 'Couples Massage', description: 'Side-by-side massage for two', price: 180, durationMinutes: 90, available: true },
    ],
  },
  {
    id: 'card_gym',
    title: 'Gym',
    category: ServiceCategory.ACTIVITY,
    icon: '🏋️',
    images: [
      'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=800&h=600&fit=crop',
    ],
    description: 'Fitness center',
    longDescription: 'State-of-the-art fitness center with cardio and strength equipment. Personal trainers available upon request.',
    location: 'Wellness Center, Level 2',
    operatingHours: '06:00-22:00',
    options: [
      { optionId: 'gym_trainer', name: 'Personal Training', description: '1-hour session with certified trainer', price: 60, durationMinutes: 60, available: true },
      { optionId: 'gym_yoga', name: 'Yoga Class', description: 'Group class, daily at 08:00', price: 20, durationMinutes: 60, available: true },
    ],
  },
  {
    id: 'card_explore',
    title: 'Explore',
    category: ServiceCategory.HOTEL_INFO,
    icon: '🗺️',
    images: [
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1555881400-69b54f8a3455?w=800&h=600&fit=crop',
    ],
    description: 'Local attractions',
    longDescription: 'Discover the best of Lisbon with our curated recommendations. From historic neighborhoods to hidden gems.',
    options: [
      { optionId: 'exp_walking', name: 'Walking Tour', description: 'Guided 3-hour city tour', price: 35, durationMinutes: 180, available: true },
      { optionId: 'exp_food', name: 'Food Tour', description: 'Taste local delicacies', price: 65, durationMinutes: 240, available: true },
    ],
  },
  {
    id: 'card_transport',
    title: 'Transport',
    category: ServiceCategory.TRANSPORT,
    icon: '🚗',
    images: [
      'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&h=600&fit=crop',
    ],
    description: 'Car service',
    longDescription: 'Premium transportation services including airport transfers, private drivers, and car rentals.',
    options: [
      { optionId: 'trans_airport', name: 'Airport Transfer', description: 'Private car to/from airport', price: 45, durationMinutes: 45, available: true },
      { optionId: 'trans_driver', name: 'Private Driver (4h)', description: 'Chauffeur at your disposal', price: 180, durationMinutes: 240, available: true },
    ],
  },
];

// =============================================================================
// MOCK BOOKINGS (Phase 2B)
// =============================================================================

export const mockBookings: Booking[] = [
  {
    bookingId: 'bkg_001',
    stayId: currentStay.stayId,
    serviceId: 'spa_swedish',
    serviceName: 'Swedish Massage',
    category: ServiceCategory.SPA,
    datetime: '2026-02-23T16:00:00',
    durationMinutes: 60,
    status: BookingStatus.CONFIRMED,
    price: 90,
    location: 'Wellness Center, Level 2',
    createdAt: '2026-02-20T10:30:00',
  },
  {
    bookingId: 'bkg_002',
    stayId: currentStay.stayId,
    serviceId: 'rest_dinner',
    serviceName: 'Dinner Reservation',
    category: ServiceCategory.RESTAURANT,
    datetime: '2026-02-23T20:00:00',
    durationMinutes: 90,
    status: BookingStatus.CONFIRMED,
    price: null,
    location: 'Main Restaurant',
    createdAt: '2026-02-20T11:00:00',
  },
  {
    bookingId: 'bkg_003',
    stayId: currentStay.stayId,
    serviceId: 'spa_couples',
    serviceName: 'Couples Massage',
    category: ServiceCategory.SPA,
    datetime: '2026-02-24T14:00:00',
    durationMinutes: 90,
    status: BookingStatus.CONFIRMED,
    price: 180,
    location: 'Wellness Center, Level 2',
    createdAt: '2026-02-21T09:00:00',
  },
];

// =============================================================================
// MOCK BILL CHARGES (Phase 2B)
// =============================================================================

export const mockBillCharges: BillCharge[] = [
  {
    chargeId: 'chg_001',
    stayId: currentStay.stayId,
    category: ChargeCategory.ROOM,
    description: 'Room 405 - Suite (5 nights)',
    amount: 1500,
    datetime: '2026-02-20T15:00:00',
  },
  {
    chargeId: 'chg_002',
    stayId: currentStay.stayId,
    category: ChargeCategory.RESTAURANT,
    description: 'Breakfast Buffet (2 guests)',
    amount: 50,
    datetime: '2026-02-21T08:00:00',
  },
  {
    chargeId: 'chg_003',
    stayId: currentStay.stayId,
    category: ChargeCategory.ROOM_SERVICE,
    description: 'Room Service - Breakfast',
    amount: 48,
    datetime: '2026-02-22T08:30:00',
    items: [
      { itemId: 'item_001', name: 'Full English Breakfast', quantity: 2, unitPrice: 18 },
      { itemId: 'item_002', name: 'Fresh Orange Juice', quantity: 2, unitPrice: 6 },
    ],
  },
  {
    chargeId: 'chg_004',
    stayId: currentStay.stayId,
    category: ChargeCategory.MINIBAR,
    description: 'Minibar',
    amount: 24,
    datetime: '2026-02-21T22:00:00',
  },
  {
    chargeId: 'chg_005',
    stayId: currentStay.stayId,
    category: ChargeCategory.SPA,
    description: 'Swedish Massage',
    amount: 90,
    datetime: '2026-02-23T16:00:00',
    bookingId: 'bkg_001',
  },
];

export const mockHotelBill: HotelBill = {
  stayId: currentStay.stayId,
  guestName: `${currentGuest.firstName} ${currentGuest.lastName}`,
  roomNumber: currentRoom.roomNumber,
  checkInDate: currentStay.checkInDate,
  checkOutDate: currentStay.checkOutDate,
  charges: mockBillCharges,
  totalAmount: mockBillCharges.reduce((sum, c) => sum + c.amount, 0),
  currency: mockHotel.currency,
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getServicesByCategory(category: ServiceCategory): ServiceCatalogItem[] {
  return mockServices.filter(s => s.category === category);
}

export function getUpcomingEvents(): CalendarEvent[] {
  const now = new Date();
  return mockCalendarEvents
    .filter(e => new Date(e.datetime) > now && e.status === CalendarEventStatus.CONFIRMED)
    .sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime());
}

export function getNextEvent(): CalendarEvent | null {
  const upcoming = getUpcomingEvents();
  return upcoming[0] || null;
}

export function formatEventTime(datetime: string): string {
  const date = new Date(datetime);
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function formatEventDate(datetime: string): string {
  const date = new Date(datetime);
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function getUnreadMessageCount(): number {
  return mockMessages.filter(m => !m.read).length;
}

export function getServiceDetailById(id: string): ServiceDetail | null {
  return mockServiceDetails.find(s => s.id === id) || null;
}
